<template>
  <div>
    <div v-if="column === 'icon'">
      <mdicon
        v-if="message.status"
        v-b-tooltip="{ title: statusTitle }"
        size="24"
        :name="statusIcon"
        :class="{
          'proxy-status-success': message.status === SUCCESS,
          'proxy-status-failure': message.status === FAILURE,
          'proxy-status-not-eligible': message.status === NOT_ELIGIBLE,
          'proxy-status-assignment-archived': message.status === ASSIGNMENT_ARCHIVED,
          'proxy-status-delivered': message.status === DELIVERED,
          'proxy-status-opened': message.status === OPENED,
        }"
      />
    </div>
    <div v-if="column === 'created_at'">
      <RowDate :date="message.created_at" />
    </div>
    <div v-if="column === 'sent_to'">
      <span class="mr-1">
        <i v-if="isSms" class="fa fa-phone" />
        <i v-else class="fa fa-envelope" />
      </span>
      {{ message.sent_to }}
    </div>
    <div v-if="column === 'data'">
      {{ messageData }}
      <a v-if="displayViewLink" :href="linkToMailPage" target="_blank"> View </a>

      <div v-if="message.error" class="pt-3 text-danger">
        <i class="fa fa-exclamation-triangle fa-fw" />
        {{ message.error }}
      </div>
    </div>
    <div v-if="column === 'shipment'">
      <RouterLink :to="shipmentRoute" class="pointer text-light-green">
        {{ message.item.shipmentNumber }}
      </RouterLink>
    </div>
    <div v-if="column === 'carrier'">
      <CarrierData :only-icon="true" :shipment="message.item" />
    </div>
  </div>
</template>

<script lang="ts">
import type { KeyAccessor } from '@/types/Helpers/Arrays'
import type { ProxyLogItem } from '@/types/Models/ProxyLog'
import type { RouteLocationRaw } from 'vue-router'
import { defineComponent, type ComputedRef, computed, type PropType } from 'vue'
import { ASSIGNMENT_ARCHIVED, DELIVERED, FAILURE, NOT_ELIGIBLE, OPENED, SUCCESS } from '@/const/MessageStatuses'
import { STAFF_SHIPMENTS } from '@/types/router'
import moment from 'moment/moment'
import RowDate from '@/views/Components/Elements/Table/RowDate.vue'
import CarrierData from '@/views/Components/Carrier/CarrierData.vue'

export default defineComponent({
  name: 'MessageRow',
  components: {
    CarrierData,
    RowDate,
  },
  props: {
    column: {
      type: String,
      required: true,
    },
    message: {
      type: Object as PropType<ProxyLogItem>,
      required: true,
    },
  },
  setup(props) {
    const isSms: ComputedRef<boolean> = computed(() => props.message.type === 'sms')
    const parsedData: ComputedRef = computed(() => (isSms.value ? null : JSON.parse(props.message.data)))
    const emailHandle: ComputedRef<string> = computed(() => parsedData.value?.handle || '')
    const emailName: ComputedRef<string> = computed(() => {
      const names: KeyAccessor<string> = {
        'labelportal.shipment.service_point_delivery': 'Parcel was diverted to a service-point',
        'labelportal.shipment.dispatched': 'Parcel is in transit',
        'labelportal.shipment.package.delivered': 'Parcel was delivered',
        default_sms: 'Default SMS',
        default_slack: 'Default Slack',
        default_email: 'Default Email',
        'labelportal.customer.report_released': 'Report released',
        'labelportal.customer.labels_released': 'Labels released',
        'labelportal.statistics.report_generated': 'Report generated',
        'labelportal.integrations.InitLogtradeIntegration': 'Logtrade integration initiated',
        'labelportal.admin.batch_assignment_created': 'Batch assignment created',
        'labelportal.admin.batch_assignment_deleted': 'Batch assignment deleted',
        'labelportal.shipment.parcel.business.dispatched': 'Parcel is in transit',
        'labelportal.shipment.parcel.consumer.available_for_collecting': 'Parcel can be collected at service-point',
        'labelportal.shipment.parcel.consumer.dispatched': 'Parcel is in transit',
        'labelportal.shipment.delivered': 'Parcel was delivered',
        'labelportal.shipment.package.dispatched': 'Package is in transit',
        'labelportal.shipment.package.service_point_diversion': 'Parcel was diverted to a service-point',
        'labelportal.shipment.package.service_point_diversion_oblivious': 'Parcel was diverted to a service-point',
        'labelportal.shipment.service_point_out_for_delivery': 'Parcel can be collected at service-point',
      }
      return names[emailHandle.value] || emailHandle.value
    })

    const messageData: ComputedRef<string> = computed(() => (isSms.value ? props.message.data : emailName.value))

    const displayViewLink: ComputedRef<boolean> = computed(
      () =>
        !isSms.value && !!emailHandle.value && moment(props.message.created_at).isAfter(moment().subtract(3, 'months')),
    )

    const linkToMailPage: ComputedRef<string> = computed(
      () => `${import.meta.env.VITE_APP_MAILER_URL}/messages/${props.message.item_id}/${emailHandle.value}/content`,
    )

    const statusIcons: KeyAccessor<string> = {
      [SUCCESS]: 'CheckCircleOutline',
      [FAILURE]: 'CloseCircleOutline',
      [NOT_ELIGIBLE]: 'AlertCircleOutline',
      [ASSIGNMENT_ARCHIVED]: 'ArchiveOutline',
      [OPENED]: 'EmailOpenOutline',
      [DELIVERED]: 'EmailOutline',
    }

    const statusTitles: KeyAccessor<string> = {
      [SUCCESS]: 'Sent',
      [FAILURE]: 'Not delivered',
      [NOT_ELIGIBLE]: 'Recipient not eligible',
      [ASSIGNMENT_ARCHIVED]: 'Assignment Archived',
      [OPENED]: 'Opened by recipient',
      [DELIVERED]: 'Delivered',
    }

    const statusIcon: ComputedRef<string> = computed(() => statusIcons[props.message.status] || 'CheckCircleOutline')
    const statusTitle: ComputedRef<string> = computed(() => statusTitles[props.message.status] || '')

    const shipmentRoute: ComputedRef<RouteLocationRaw> = computed(() => ({
      name: STAFF_SHIPMENTS,
      query: { shipmentNumber: props.message.item.shipmentNumber },
    }))

    return {
      isSms,
      statusIcon,
      statusTitle,
      messageData,
      displayViewLink,
      linkToMailPage,
      ASSIGNMENT_ARCHIVED,
      NOT_ELIGIBLE,
      DELIVERED,
      FAILURE,
      SUCCESS,
      OPENED,
      shipmentRoute,
    }
  },
})
</script>
